import React from 'react';

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-3xl mx-auto px-6 py-12">
        <div className="mb-8">
          <a href="/" className="text-sm text-blue-600 hover:text-blue-800">&larr; Back to Banyak Group</a>
        </div>
        <div className="bg-white rounded-xl shadow-sm border p-8 space-y-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Privacy Policy</h1>
            <p className="text-sm text-gray-500 mt-1">Last updated: July 21, 2026</p>
          </div>

          <section className="space-y-3">
            <h2 className="text-lg font-semibold text-gray-900">1. Introduction</h2>
            <p className="text-sm text-gray-700 leading-relaxed">
              Banyak Group ("we," "our," or "us") operates the recruitment and pipeline management platform at app.banyak.group. 
              This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our platform.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-lg font-semibold text-gray-900">2. Information We Collect</h2>
            <p className="text-sm text-gray-700 leading-relaxed">We collect the following types of information:</p>
            <p className="text-sm text-gray-700 leading-relaxed">
              <strong>Account Information:</strong> Name, email address, phone number, firm affiliation, and user role when you create an account or are invited to the platform.
            </p>
            <p className="text-sm text-gray-700 leading-relaxed">
              <strong>Candidate Data:</strong> Professional information about candidates including names, job titles, employment history, compensation details, contact information, and profile photos uploaded by authorized users.
            </p>
            <p className="text-sm text-gray-700 leading-relaxed">
              <strong>Calendar Data:</strong> When you connect your Google Calendar or Microsoft Outlook account, we access calendar availability and create events on your behalf. We do not read or store the contents of your existing calendar events.
            </p>
            <p className="text-sm text-gray-700 leading-relaxed">
              <strong>Phone Numbers and Mobile Information:</strong> We collect phone numbers provided by users during account registration, within account settings, or as part of candidate profiles. Mobile phone numbers may be used to send SMS text messages related to recruitment activities, interview notifications, and platform communications. We do not sell, share, or rent mobile phone numbers or SMS consent information to any third parties for marketing or promotional purposes.
            </p>
            <p className="text-sm text-gray-700 leading-relaxed">
              <strong>Voice Call Data:</strong> When users place calls through the platform's built-in calling feature, we may collect call metadata including phone numbers, call duration, timestamps, and call status. If call recording is enabled by the user, we store audio recordings and may generate AI-powered transcriptions of those recordings for recruitment documentation purposes.
            </p>
            <p className="text-sm text-gray-700 leading-relaxed">
              <strong>Usage Data:</strong> We collect information about how you interact with the platform, including pages visited, features used, and actions taken.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-lg font-semibold text-gray-900">3. How We Use Your Information</h2>
            <p className="text-sm text-gray-700 leading-relaxed">We use the information we collect to:</p>
            <p className="text-sm text-gray-700 leading-relaxed">
              Provide, maintain, and improve our recruitment management platform; facilitate communication between recruiters, clients, and candidates; 
              schedule interviews and meetings via calendar integrations; send transactional emails such as notifications and account updates; 
              send SMS text messages related to recruitment outreach, interview scheduling, and platform notifications;
              facilitate in-app voice calls and, when enabled, record and transcribe calls for recruitment documentation;
              and ensure the security and integrity of our platform.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-lg font-semibold text-gray-900">4. Calendar Integration</h2>
            <p className="text-sm text-gray-700 leading-relaxed">
              Our platform integrates with Google Calendar and Microsoft Outlook to schedule interviews and meetings. When you authorize calendar access, we request permissions to view your availability (free/busy status) and create calendar events. 
              We only access calendar data when you explicitly use the scheduling features. Calendar tokens are stored securely and can be revoked at any time through your profile settings or directly through your Google/Microsoft account.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-lg font-semibold text-gray-900">5. SMS and Text Messaging</h2>
            <p className="text-sm text-gray-700 leading-relaxed">
              <strong>SMS messaging is optional.</strong> Banyak Group LTD's recruitment
              services do not require SMS consent. Candidates may work with our recruiters,
              be considered for any role, and use any feature of our platform without
              receiving SMS messages.
            </p>
            <p className="text-sm text-gray-700 leading-relaxed">
              Banyak Group LTD may send SMS text messages only to candidates and users who
              have explicitly opted in. Consent is collected two ways, both optional:
            </p>
            <p className="text-sm text-gray-700 leading-relaxed">
              <strong>Self-serve opt-in.</strong> Candidates may join our talent network at{' '}
              <a href="/talent-network" className="text-blue-600 hover:underline">https://app.banyak.group/talent-network</a>.
              Joining requires only a name and email; a mobile number and an unchecked-by-default
              SMS consent checkbox are presented as an optional add-on. The signup completes
              without providing a number or agreeing to SMS, so users can access the service
              without consenting.
            </p>
            <p className="text-sm text-gray-700 leading-relaxed">
              <strong>Recruiter-initiated opt-in.</strong> When a recruiter is already in
              contact with a candidate, the recruiter may send a personalized consent link
              (<a href="/sms-consent" className="text-blue-600 hover:underline">https://app.banyak.group/sms-consent</a>)
              tied to that candidate's record. The candidate enters their mobile number and
              ticks an unchecked-by-default consent checkbox containing the verbatim opt-in
              language, and declining is a supported one-click path.
            </p>
            <p className="text-sm text-gray-700 leading-relaxed">
              In both cases no SMS is sent without a granted consent record on file; the
              consent gate is enforced server-side before every outbound message. Full program
              details are available at{' '}
              <a href="/messaging" className="text-blue-600 hover:underline">https://app.banyak.group/messaging</a>.
            </p>
            <p className="text-sm text-gray-700 leading-relaxed">
              SMS messages may include recruitment outreach, job opportunities, interview
              notifications and scheduling, application status updates, recruiter
              communications, and other role-specific recruitment messages. Message
              frequency varies based on recruitment activity (typically 1–4 messages per
              active conversation). Message and data rates may apply.
            </p>
            <p className="text-sm text-gray-700 leading-relaxed">
              For each opt-in or decline record we store the mobile number (if provided),
              the verbatim consent text shown at opt-in (for opt-ins only), the IP address
              and user agent of the request, and a UTC timestamp. This audit record is used
              solely to verify the user's choice and is retained for compliance purposes.
            </p>
            <p className="text-sm text-gray-700 leading-relaxed">
              Users may opt out of receiving SMS messages at any time by replying STOP to
              any message; the consent record is marked revoked and all future send
              attempts to the same number are blocked. For assistance, recipients may
              reply HELP or email oban@banyak.group.
            </p>
            <p className="text-sm text-gray-700 leading-relaxed">
              <strong>No mobile information will be shared with third parties or
              affiliates for marketing or promotional purposes.</strong> Information
              sharing to subcontractors in support services, such as customer service,
              is permitted. All other use case categories exclude text messaging
              originator opt-in data and consent; this information will not be shared
              with any third parties.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-lg font-semibold text-gray-900">6. Voice Calling and Recordings</h2>
            <p className="text-sm text-gray-700 leading-relaxed">
              The platform provides an in-app voice calling feature powered by Twilio. When a recruiter initiates a call, 
              they may choose to enable call recording. If recording is enabled, both parties on the call are recorded. 
              Recordings may be processed using AI-powered transcription services (OpenAI Whisper) to generate text transcripts. 
              Call recordings and transcripts are stored securely and are accessible only to authorized users within the recruiting organization. 
              Recordings are used solely for recruitment documentation and quality purposes.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-lg font-semibold text-gray-900">7. Data Sharing</h2>
            <p className="text-sm text-gray-700 leading-relaxed">
              We share candidate information between recruitment firms and their client firms only as part of the established recruitment workflow. 
              We do not sell, rent, or trade personal information to third parties. We do not sell, share, or provide mobile phone numbers or SMS consent information to third parties for marketing or any non-operational purpose.
              We may share information with service providers (such as AWS for hosting and email delivery, Twilio for voice and SMS services, and OpenAI for transcription services) who assist in operating our platform, subject to confidentiality obligations.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-lg font-semibold text-gray-900">8. Data Security</h2>
            <p className="text-sm text-gray-700 leading-relaxed">
              We implement appropriate technical and organizational measures to protect your information, including encrypted data transmission (TLS/SSL), 
              secure database storage on AWS infrastructure, role-based access controls, and secure token-based authentication. 
              Voice recordings and transcripts are stored with the same level of security as other platform data. 
              However, no method of electronic transmission or storage is 100% secure.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-lg font-semibold text-gray-900">9. Data Retention</h2>
            <p className="text-sm text-gray-700 leading-relaxed">
              We retain your information for as long as your account is active or as needed to provide our services. 
              Call recordings and transcripts are retained as part of recruitment documentation and may be deleted upon request.
              You may request deletion of your account and associated data by contacting us.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-lg font-semibold text-gray-900">10. Your Rights</h2>
            <p className="text-sm text-gray-700 leading-relaxed">
              You have the right to access, correct, or delete your personal information; disconnect third-party calendar integrations at any time; 
              opt out of SMS communications by replying STOP to any text message;
              opt out of non-essential communications; and request a copy of your data.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-lg font-semibold text-gray-900">11. Changes to This Policy</h2>
            <p className="text-sm text-gray-700 leading-relaxed">
              We may update this Privacy Policy from time to time. We will notify users of significant changes via email or through the platform.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-lg font-semibold text-gray-900">12. Contact Us</h2>
            <p className="text-sm text-gray-700 leading-relaxed">
              If you have questions about this Privacy Policy, please contact us at <a href="mailto:oban@banyak.group" className="text-blue-600 hover:underline">oban@banyak.group</a>.
            </p>
          </section>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
